/* ==========================================================================
   LOYAL · WAVES
   A field of undulating lines drawn to canvas, used as the FILL inside the
   masked wordmark. Vanilla, no framework, no build step.

   WHY THIS EXISTS AND WHAT IT REPLACED. The masked wordmark first used a video
   of a dark room. It was invisible: the room measured 17 percent grey, and near
   black inside a dark letterform on a black page shows nothing. The reference
   Kailin sent works because BRIGHT foliage sits inside a dark mark. The fill has
   to out-contrast the ground or there is nothing to see.

   Line art on black is contrast by construction. It also weighs a few kilobytes
   instead of four megabytes, needs no video file, no CDN and no hotlink, and it
   responds to the cursor, which a video cannot.

   WHY IT IS ON-THESIS AND NOT A GIMMICK. Kailin read the motion as bodily.
   For this brand that is the point: the softest possible motion set against a
   brand whose whole word is SOLID. Tension, not decoration.

   NO GLASS ANYWHERE IN HERE. MOTION-SYSTEM section 7 bans it by name, and the
   reason is the thesis: "Solid in a world of air. A glass button is an argument
   against the product it is selling."

   PERFORMANCE, because a permanent requestAnimationFrame loop on a storefront
   hero is a real cost and not a theoretical one:
     - the loop STOPS when the canvas scrolls out of view (IntersectionObserver)
     - the loop STOPS when the tab is hidden (visibilitychange)
     - reduced motion draws ONE frame and never starts the loop
     - the grid is rebuilt on resize only, never per frame
   ========================================================================== */

(function () {
  'use strict';

  var CONFIG = {
    xGap: 6,
    yGap: 20,
    widthPad: 200,
    heightPad: 30,

    waveTimeX: 0.0125,
    waveNoiseX: 0.002,
    waveTimeY: 0.005,
    waveNoiseY: 0.0015,
    waveMagnitude: 12,
    ampX: 32,
    ampY: 11,

    pointerRadius: 175,
    pointerFalloff: 0.001,
    pointerForce: 0.00065,
    pointerSmoothing: 0.1,
    pointerMaxVelocity: 100,

    tension: 0.005,
    friction: 0.925,
    displacement: 2,
    maxDisplacement: 100,

    lineWidth: 0.9,
  };

  /* ---- Perlin noise. Classic implementation, seeded, no dependency. ---- */
  function Noise(seed) {
    this.p = new Uint8Array(512);
    this.grad3 = [
      [1, 1, 0], [-1, 1, 0], [1, -1, 0], [-1, -1, 0],
      [1, 0, 1], [-1, 0, 1], [1, 0, -1], [-1, 0, -1],
      [0, 1, 1], [0, -1, 1], [0, 1, -1], [0, -1, -1],
    ];
    var s = seed > 0 && seed < 1 ? seed : Math.random();
    var base = new Uint8Array(256);
    var i, j, k;
    for (i = 0; i < 256; i++) base[i] = i;
    for (i = 0; i < 256; i++) {
      j = Math.floor(s * (i + 1)) % 256;
      k = base[i];
      base[i] = base[j];
      base[j] = k;
    }
    for (i = 0; i < 512; i++) this.p[i] = base[i & 255];
  }

  Noise.prototype.dot = function (g, x, y) {
    return g[0] * x + g[1] * y;
  };

  Noise.prototype.perlin2 = function (x, y) {
    var X = Math.floor(x) & 255;
    var Y = Math.floor(y) & 255;
    x -= Math.floor(x);
    y -= Math.floor(y);
    var fade = function (t) { return t * t * t * (t * (t * 6 - 15) + 10); };
    var lerp = function (a, b, t) { return a + t * (b - a); };
    var u = fade(x);
    var v = fade(y);
    var p = this.p;
    var g = this.grad3;
    var n00 = this.dot(g[p[X + p[Y]] % 12], x, y);
    var n01 = this.dot(g[p[X + p[Y + 1]] % 12], x, y - 1);
    var n10 = this.dot(g[p[X + 1 + p[Y]] % 12], x - 1, y);
    var n11 = this.dot(g[p[X + 1 + p[Y + 1]] % 12], x - 1, y - 1);
    return lerp(lerp(n00, n10, u), lerp(n01, n11, u), v);
  };

  /* ---- One field per canvas ---- */
  function Field(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.noise = new Noise(Math.random());
    this.lines = [];
    this.raf = null;
    this.running = false;
    this.visible = false;
    this.box = null;

    // Colour comes from the element, which reads a token in CSS. Never a
    // literal here, so brand-guard can see it and a theme change reaches it.
    var declared = getComputedStyle(canvas).getPropertyValue('--wave-line');
    this.lineColor = (declared && declared.trim()) || '#F5F2EB';

    this.pointer = {
      x: -200, y: 0, lx: 0, ly: 0, sx: 0, sy: 0, vs: 0, a: 0, set: false,
    };

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.onResize = this.resize.bind(this);
    this.onMove = this.move.bind(this);
    this.onVisibility = this.visibility.bind(this);

    this.resize();
    this.bind();
  }

  Field.prototype.bind = function () {
    window.addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibility);

    if (!this.reduced) {
      window.addEventListener('mousemove', this.onMove, { passive: true });
    }

    var self = this;
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        self.sync();
      }, { threshold: 0 });
      this.io.observe(this.canvas);
    } else {
      this.visible = true;
      this.sync();
    }
  };

  Field.prototype.visibility = function () {
    this.sync();
  };

  // The loop runs ONLY when the canvas is on screen and the tab is focused.
  // A hero animation that keeps burning frames three sections down is a battery
  // cost the visitor never agreed to.
  Field.prototype.sync = function () {
    var should = this.visible && !document.hidden && !this.reduced;
    if (should && !this.running) {
      this.running = true;
      var self = this;
      this.raf = requestAnimationFrame(function (t) { self.tick(t); });
    } else if (!should && this.running) {
      this.running = false;
      cancelAnimationFrame(this.raf);
    }
  };

  Field.prototype.resize = function () {
    var rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    // Cap the backing store at 2x. A 3x phone painting a full width canvas of
    // curves every frame is where this goes from cheap to expensive.
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.box = rect;
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.build();
    if (this.reduced) this.draw();
  };

  Field.prototype.build = function () {
    var w = this.box.width;
    var h = this.box.height;
    var oW = w + CONFIG.widthPad;
    var oH = h + CONFIG.heightPad;
    var cols = Math.ceil(oW / CONFIG.xGap);
    var rows = Math.ceil(oH / CONFIG.yGap);
    var x0 = (w - CONFIG.xGap * cols) / 2;
    var y0 = (h - CONFIG.yGap * rows) / 2;

    this.lines = [];
    for (var i = 0; i <= cols; i++) {
      var pts = [];
      for (var j = 0; j <= rows; j++) {
        pts.push({
          x: x0 + CONFIG.xGap * i,
          y: y0 + CONFIG.yGap * j,
          wx: 0, wy: 0,
          cx: 0, cy: 0, cvx: 0, cvy: 0,
        });
      }
      this.lines.push(pts);
    }
  };

  Field.prototype.move = function (e) {
    if (!this.box) return;
    var p = this.pointer;
    p.x = e.clientX - this.box.left;
    p.y = e.clientY - this.box.top;
    if (!p.set) {
      p.sx = p.lx = p.x;
      p.sy = p.ly = p.y;
      p.set = true;
    }
  };

  Field.prototype.step = function (time) {
    var p = this.pointer;
    var C = CONFIG;

    for (var i = 0; i < this.lines.length; i++) {
      var pts = this.lines[i];
      for (var j = 0; j < pts.length; j++) {
        var pt = pts[j];

        var m = this.noise.perlin2(
          (pt.x + time * C.waveTimeX) * C.waveNoiseX,
          (pt.y + time * C.waveTimeY) * C.waveNoiseY
        ) * C.waveMagnitude;
        pt.wx = Math.cos(m) * C.ampX;
        pt.wy = Math.sin(m) * C.ampY;

        var dx = pt.x - p.sx;
        var dy = pt.y - p.sy;
        var d = Math.hypot(dx, dy);
        var r = Math.max(C.pointerRadius, p.vs);
        if (d < r) {
          var falloff = 1 - d / r;
          var force = Math.cos(d * C.pointerFalloff) * falloff;
          var f = force * r * p.vs * C.pointerForce;
          pt.cvx += Math.cos(p.a) * f;
          pt.cvy += Math.sin(p.a) * f;
        }

        pt.cvx += (0 - pt.cx) * C.tension;
        pt.cvy += (0 - pt.cy) * C.tension;
        pt.cvx *= C.friction;
        pt.cvy *= C.friction;
        pt.cx += pt.cvx * C.displacement;
        pt.cy += pt.cvy * C.displacement;
        pt.cx = Math.min(C.maxDisplacement, Math.max(-C.maxDisplacement, pt.cx));
        pt.cy = Math.min(C.maxDisplacement, Math.max(-C.maxDisplacement, pt.cy));
      }
    }
  };

  Field.prototype.draw = function () {
    var ctx = this.ctx;
    if (!this.box) return;
    ctx.clearRect(0, 0, this.box.width, this.box.height);
    ctx.beginPath();
    ctx.strokeStyle = this.lineColor;
    ctx.lineWidth = CONFIG.lineWidth;

    for (var i = 0; i < this.lines.length; i++) {
      var pts = this.lines[i];
      var first = pts[0];
      ctx.moveTo(first.x + first.wx, first.y + first.wy);
      for (var j = 0; j < pts.length - 1; j++) {
        var a = pts[j];
        var b = pts[j + 1];
        var ax = a.x + a.wx + a.cx;
        var ay = a.y + a.wy + a.cy;
        var bx = b.x + b.wx + b.cx;
        var by = b.y + b.wy + b.cy;
        ctx.quadraticCurveTo(ax, ay, (ax + bx) / 2, (ay + by) / 2);
      }
    }
    ctx.stroke();
  };

  Field.prototype.tick = function (time) {
    if (!this.running) return;
    var p = this.pointer;
    var C = CONFIG;

    p.sx += (p.x - p.sx) * C.pointerSmoothing;
    p.sy += (p.y - p.sy) * C.pointerSmoothing;
    var dx = p.sx - p.lx;
    var dy = p.sy - p.ly;
    var d = Math.hypot(dx, dy);
    p.vs += (d - p.vs) * C.pointerSmoothing;
    p.vs = Math.min(C.pointerMaxVelocity, p.vs);
    p.a = Math.atan2(dy, dx);
    p.lx = p.sx;
    p.ly = p.sy;

    this.step(time);
    this.draw();

    var self = this;
    this.raf = requestAnimationFrame(function (t) { self.tick(t); });
  };

  function init() {
    var nodes = document.querySelectorAll('[data-loyal-waves]');
    for (var i = 0; i < nodes.length; i++) new Field(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
